<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { formatDate, formatDateTime, restorationStages, type ChecklistItemResult, type MediaPhase } from '@ck/domain'
import { CkAlert, CkCard, CkProgressRing, CkStepper, CkTabs } from '@ck/ui'
import { accountsApi, checklistsApi, isCustomerVisibleMedia, isCustomerVisibleTimeline, mediaApi } from '@ck/api-client'
import { identity } from '@ck/auth'
import { useActiveWorkOrder } from '../composables/useActiveWorkOrder'
import PortalHeader from '../components/PortalHeader.vue'
import RepairApprovals from '../components/RepairApprovals.vue'

const customerName = ref(identity().name ?? '')
onMounted(async () => {
  try {
    const me = await accountsApi.me()
    if (me.name) customerName.value = me.name
  } catch {
    // mantém o nome vazio quando /accounts/me não está disponível (ex.: authRequired=false)
  }
})

const { workOrderQuery, activeWorkOrder, detailQuery } = useActiveWorkOrder()
const stageIndex = computed(() => {
  const index = restorationStages.findIndex((stage) => stage.title === activeWorkOrder.value?.currentStage)
  return index === -1 ? 0 : index
})
const timeline = computed(() => [...(detailQuery.data.value?.timeline ?? []).filter(isCustomerVisibleTimeline)].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)))

const checklistQuery = useQuery(computed(() => ({
  queryKey: ['checklists', activeWorkOrder.value?.id],
  queryFn: () => checklistsApi.get(activeWorkOrder.value!.id),
  enabled: !!activeWorkOrder.value
})))
function inspectionCounts(items: ChecklistItemResult[]) {
  return {
    ok: items.filter((i) => i.status === 'ok').length,
    warn: items.filter((i) => i.status === 'attention').length,
    fail: items.filter((i) => i.status === 'rejected').length
  }
}

const phase = ref<MediaPhase>('before')
const phaseTabs = [
  { key: 'before', label: 'Antes' },
  { key: 'during', label: 'Durante' },
  { key: 'after', label: 'Depois' }
] as const
const mediaQuery = useQuery(computed(() => ({
  queryKey: ['media', activeWorkOrder.value?.id],
  queryFn: () => mediaApi.list(activeWorkOrder.value!.id),
  enabled: !!activeWorkOrder.value
})))
const photosInPhase = computed(() => (mediaQuery.data.value ?? []).filter(isCustomerVisibleMedia).filter((asset) => asset.phase === phase.value))
const mediaUrls = ref<Record<string, string>>({})
watch(() => mediaQuery.data.value, async (assets, _previous, onCleanup) => {
  const urls: Record<string, string> = {}
  for (const asset of assets ?? []) {
    try { urls[asset.id] = URL.createObjectURL(await mediaApi.fetchFile(asset.id)) } catch { /* ignora falha ao baixar uma foto específica */ }
  }
  mediaUrls.value = urls
  onCleanup(() => Object.values(urls).forEach((url) => URL.revokeObjectURL(url)))
}, { immediate: true })
</script>

<template>
  <div class="portal">
    <PortalHeader :customer-name="customerName"/>
    <CkAlert v-if="workOrderQuery.isError.value" tone="error" class="alert">Não foi possível carregar sua ordem de serviço. Tente novamente em instantes.</CkAlert>
    <p v-else-if="workOrderQuery.isPending.value" class="status-message">Carregando…</p>
    <p v-else-if="!activeWorkOrder" class="status-message">Nenhuma ordem de serviço publicada para você no momento.</p>
    <main v-else>
      <section class="hero">
        <div>
          <div class="ck-eyebrow">Em restauração · {{ activeWorkOrder.number }}</div>
          <h1>{{ activeWorkOrder.vehicle.brand }} {{ activeWorkOrder.vehicle.model }} {{ activeWorkOrder.vehicle.year }}</h1>
          <p class="ck-serif-italic">“Seu clássico, restaurado com método.”</p>
          <dl>
            <div><dt>Placa</dt><dd class="ck-code">{{ activeWorkOrder.vehicle.plate }}</dd></div>
            <div><dt>Previsão</dt><dd>{{ formatDate(activeWorkOrder.expectedDelivery) }}</dd></div>
            <div><dt>Etapa atual</dt><dd>{{ activeWorkOrder.currentStage ?? '—' }}</dd></div>
          </dl>
        </div>
        <div class="ring-wrap"><CkProgressRing :value="activeWorkOrder.progress"/></div>
      </section>

      <CkCard class="stages">
        <div class="ck-eyebrow">Progresso da restauração</div>
        <CkStepper :stages="restorationStages" :current-index="stageIndex" orientation="horizontal" class="stepper"/>
      </CkCard>

      <div class="grid">
        <CkCard>
          <h2>Galeria</h2>
          <CkTabs :tabs="phaseTabs" :model-value="phase" @update:model-value="(v) => (phase = v as MediaPhase)"/>
          <div v-if="photosInPhase.length" class="gallery"><img v-for="asset in photosInPhase" :key="asset.id" :src="mediaUrls[asset.id]" :alt="asset.fileName"></div>
          <p v-else class="empty-inline">Nenhuma foto publicada nesta fase ainda.</p>
        </CkCard>
        <CkCard>
          <h2>Resumo da inspeção</h2>
          <ul v-if="checklistQuery.data.value?.length">
            <li v-for="stage in checklistQuery.data.value" :key="stage.id">
              <span>{{ stage.title }}</span>
              <span class="counts">
                <span class="ok">{{ inspectionCounts(stage.items).ok }}✓</span>
                <span class="warn">{{ inspectionCounts(stage.items).warn }}!</span>
                <span class="fail">{{ inspectionCounts(stage.items).fail }}✕</span>
              </span>
            </li>
          </ul>
          <p v-else class="empty-inline">Inspeção ainda não iniciada.</p>
        </CkCard>
      </div>

      <RepairApprovals :work-order-id="activeWorkOrder.id" :stages="detailQuery.data.value?.stages" />

      <CkCard class="updates">
        <h2>Atualizações</h2>
        <article v-for="event in timeline" :key="event.id"><span class="dot"/><div><small class="ck-code">{{ formatDateTime(event.occurredAt).toUpperCase() }}</small><p>{{ event.description }}</p></div></article>
        <p v-if="timeline.length === 0" class="empty-inline">Nenhuma atualização publicada ainda.</p>
      </CkCard>
    </main>
  </div>
</template>

<style scoped>
.portal { min-height: 100vh; padding-bottom: 40px; }
.alert { margin: 24px max(24px, calc((100vw - 1100px) / 2)) 0; }
main { max-width: 1100px; margin: 28px auto; padding: 0 20px; }
.hero { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 32px; border-radius: var(--radius-xl); background: var(--ck-ink-800); color: var(--ck-text-on-dark); box-shadow: var(--shadow-lg); }
.hero h1 { margin: 8px 0; font-size: clamp(30px, 5vw, 48px); }
.hero .ck-eyebrow { color: var(--ck-amber-400); }
.hero .ck-serif-italic { color: #d9aaa2; }
.hero dl { display: flex; gap: 32px; margin: 24px 0 0; flex-wrap: wrap; }
.hero dt { font-size: 10px; text-transform: uppercase; letter-spacing: var(--ls-widest); color: var(--ck-text-on-dark-muted); }
.hero dd { margin: 4px 0 0; font-weight: 700; }
.ring-wrap { flex-shrink: 0; background: var(--ck-cream-50); border-radius: 99px; padding: 10px; }
.stages { margin-top: 24px; }
.stages .ck-eyebrow { margin-bottom: 18px; }
.stepper { padding: 0 6px; }
.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 24px; }
h2 { margin: 0 0 14px; font-size: 18px; }
.gallery { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 14px; }
.gallery img { height: 130px; width: 100%; object-fit: cover; border-radius: var(--radius-md); background: var(--ck-ink-800); }
.empty-inline { color: var(--ck-text-muted); font-size: 13px; }
ul { padding: 0; list-style: none; margin: 0; }
li { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid var(--ck-cream-200); font-size: 13.5px; }
.counts { display: flex; gap: 10px; font-family: var(--font-mono); font-size: 12.5px; }
.counts .ok { color: var(--ck-ok-500); }
.counts .warn { color: var(--ck-warn-500); }
.counts .fail { color: var(--ck-fail-500); }
.updates { margin-top: 24px; }
.updates article { display: flex; gap: 14px; padding-bottom: 16px; }
.updates article:last-child { padding-bottom: 0; }
.dot { width: 10px; height: 10px; margin-top: 5px; border-radius: 50%; background: var(--ck-orange-500); flex-shrink: 0; }
.updates p { margin: 5px 0 0; }
.status-message { padding: 32px max(24px, calc((100vw - 1100px) / 2)); color: var(--ck-text-muted); }
@media (max-width: 760px) {
  .hero { flex-direction: column; align-items: flex-start; gap: 16px; }
  .ring-wrap { align-self: center; }
  .grid { grid-template-columns: 1fr; }
}
</style>
